import { useState, useCallback, useEffect } from 'react';
import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
  useQueryClient
} from 'react-query';
import type { UnreadNotification } from 'api/messageCommunication/types';

import useNotificationReceiver from 'hooks/notifications/useNotificationReceiver';
import useGetUnreadRealTime from 'hooks/messageCommunication/useGetUnreadRealTime';
import useSendReadMessage from 'hooks/messageCommunication/useSendReadMessage';
import { PagedList } from 'interfaces/general';
import { NotificationKey } from 'helpers/app.config';
import { KEYS } from 'helpers/react-query.config';

type Props = {
  receiverUserId: number;
  open: boolean;
};

type UseRealTimeNotificationsReturn = {
  actualRealTimePageNumber: number;
  handleLoadMoreRealTimeNotifications: () => void;
  handleReadAllRealTimeNotifications: () => void;
  handleReadRealTimeNotification: (id: number) => void;
  isRealTimeNotificationsFetching: boolean;
  isRealTimeNotificationsLoading: boolean;
  realTimeNotifications: UnreadNotification[];
  realTimeTotalCount: number;
  setRealTimeNotifications: (notifications: UnreadNotification[]) => void;
  setRealTimeNotificationsParams: (params: object) => void;
  setRealTimeTotalCount: (count: number) => void;
  refetchRealTimeNotifications: <TPageData>(
    options?: RefetchOptions & RefetchQueryFilters<TPageData>
  ) => Promise<QueryObserverResult<PagedList<UnreadNotification>, unknown>>;
};

/**
 * A custom hook that manages real-time notifications for a user.
 *
 * @param {Object} props - The properties for the hook.
 * @param {string} props.receiverUserId - The ID of the user receiving notifications.
 * @param {boolean} props.open - A flag indicating whether the notifications are open.
 *
 * @type {Object} UseRealTimeNotificationsReturn
 * @property {number} actualRealTimePageNumber - The current page number of real-time notifications.
 * @property {Function} handleLoadMoreRealTimeNotifications - Loads more notifications.
 * @property {Function} handleReadAllRealTimeNotifications - Marks all notifications as read.
 * @property {Function} handleReadRealTimeNotification - Marks a specific notification as read.
 * @property {boolean} isRealTimeNotificationsFetching - Indicates if notifications are currently being fetched.
 * @property {boolean} isRealTimeNotificationsLoading - Indicates if notifications are loading.
 * @property {UnreadNotification[]} realTimeNotifications - The list of real-time notifications.
 * @property {number} realTimeTotalCount - The total count of real-time notifications.
 * @property {Function} setRealTimeNotifications - Sets the list of real-time notifications.
 * @property {Function} setRealTimeNotificationsParams - Sets the parameters for fetching notifications.
 * @property {Function} setRealTimeTotalCount - Sets the total count of notifications.
 * @property {Function} refetchRealTimeNotifications - Refetches the real-time notifications.
 * @returns {UseRealTimeNotificationsReturn}
 */

function useRealTimeNotifications({
  receiverUserId,
  open
}: Props): UseRealTimeNotificationsReturn {
  const [realTimeNotifications, setRealTimeNotifications] = useState<
    UnreadNotification[]
  >([]);
  const [realTimeTotalCount, setRealTimeTotalCount] = useState(0);
  const [isRealTimeEnableToFetch, setIsRealTimeEnableToFetch] = useState(true);
  const [isNotificationExists, setNotificationExists] = useState(false);

  const { sendReadMessage } = useSendReadMessage();
  const queryClient = useQueryClient();

  const {
    isUnreadNotificationsFetching: isRealTimeNotificationsFetching,
    isUnreadNotificationsLoading: isRealTimeNotificationsLoading,
    params: realTimeParams,
    refetchUnreadNotifications: refetchRealTimeNotifications,
    setUnreadNotificationsParams: setRealTimeNotificationsParams
  } = useGetUnreadRealTime({
    enabled: isRealTimeEnableToFetch,
    receiverUserId,
    realTimeNotifications,
    setEnabled: setIsRealTimeEnableToFetch,
    setNotifications: setRealTimeNotifications,
    setTotalCount: setRealTimeTotalCount
  });

  useEffect(() => {
    if (isNotificationExists) {
      setRealTimeTotalCount((prevState) => prevState - 1);
      setNotificationExists(false);
    }
  }, [isNotificationExists]);

  useEffect(() => {
    if (open) {
      setIsRealTimeEnableToFetch(true);
    }
  }, [open]);

  const handleReceivedNotification = useCallback(
    (notification: UnreadNotification) => {
      // Si es una notificación de actualización masiva completada,
      // invalidar la query de tickets para refrescar la tabla
      if (
        notification.notificationKey === NotificationKey.BulkUpdateCompleted
      ) {
        queryClient
          .invalidateQueries(KEYS.ticket.tickets.generalSearch())
          .catch(() => {});
      }

      setRealTimeTotalCount((prevState) => prevState + 1);

      setRealTimeNotifications((prevNotifications) => {
        const isNotificationExist = prevNotifications.some(
          (prevNotification) =>
            prevNotification.notificationId === notification.notificationId
        );

        if (isNotificationExist) {
          setNotificationExists(true);

          return prevNotifications;
        }

        const uploadOldNotifications = prevNotifications.filter(
          (prevNotification) =>
            prevNotification.ticketId !== notification.ticketId
        );

        uploadOldNotifications.unshift(notification);

        return uploadOldNotifications;
      });
    },
    [queryClient]
  );

  useNotificationReceiver({
    messageHandler: handleReceivedNotification,
    receiverUserId: receiverUserId?.toString()
  });

  const handleReadRealTimeNotification = useCallback(
    (notificationId: number) => {
      setRealTimeTotalCount((prevState) => prevState - 1);
      setRealTimeNotifications((prevNotifications) =>
        prevNotifications.filter(
          (notification) => notification.notificationId !== notificationId
        )
      );

      if (realTimeTotalCount !== 0 && realTimeNotifications.length === 1) {
        setRealTimeNotificationsParams({ pageNumber: 1 });
      }

      sendReadMessage(
        { receiverUserId, notificationIds: [notificationId] },
        {
          onSuccess: async () => {
            if (
              realTimeTotalCount !== 0 &&
              realTimeNotifications.length === 1
            ) {
              await refetchRealTimeNotifications();
            }
          }
        }
      );
    },
    [
      realTimeNotifications.length,
      realTimeTotalCount,
      receiverUserId,
      refetchRealTimeNotifications,
      sendReadMessage,
      setRealTimeNotificationsParams
    ]
  );

  const handleReadAllRealTimeNotifications = useCallback(() => {
    const notificationsWithNeedsAttention = realTimeNotifications.filter(
      (notification) => notification.needsAttention
    );

    const filteredByNeedsAttention = realTimeNotifications.filter(
      (notification) => !notification.needsAttention
    );

    const notificationIds = filteredByNeedsAttention.map(
      (notification) => notification.notificationId
    );

    if (!notificationIds.length) return;

    setRealTimeNotifications(notificationsWithNeedsAttention);

    setRealTimeTotalCount(
      (prevState) =>
        prevState -
        realTimeNotifications.length +
        notificationsWithNeedsAttention.length
    );

    setRealTimeNotificationsParams({ pageNumber: 1 });

    sendReadMessage(
      {
        receiverUserId,
        notificationIds
      },
      {
        onSuccess: async () => {
          const actualTotalCount =
            realTimeTotalCount - realTimeNotifications.length;

          if (actualTotalCount !== 0) {
            await refetchRealTimeNotifications();
          }
        }
      }
    );
  }, [
    realTimeNotifications,
    realTimeTotalCount,
    receiverUserId,
    refetchRealTimeNotifications,
    sendReadMessage,
    setRealTimeNotificationsParams
  ]);

  const handleLoadMoreRealTimeNotifications = useCallback(() => {
    setRealTimeNotificationsParams({
      pageNumber: realTimeParams.pageNumber + 1
    });

    setIsRealTimeEnableToFetch(true);
  }, [realTimeParams.pageNumber, setRealTimeNotificationsParams]);

  return {
    actualRealTimePageNumber: realTimeParams.pageNumber,
    handleLoadMoreRealTimeNotifications,
    handleReadAllRealTimeNotifications,
    handleReadRealTimeNotification,
    isRealTimeNotificationsFetching,
    isRealTimeNotificationsLoading,
    realTimeNotifications,
    realTimeTotalCount,
    setRealTimeNotifications,
    setRealTimeNotificationsParams,
    setRealTimeTotalCount,
    refetchRealTimeNotifications
  };
}

export default useRealTimeNotifications;
